// Typed fetch wrapper over the generated OpenAPI types (ADR-032, shared by both shells per
// ADR-101). Same origin only: the session is the shell's HttpOnly cookie the browser sends by
// itself, and the only secret the page ever holds is the CSRF token, in memory (./csrf.ts). The
// AllCode admin reaches the API at the template path (Caddy proxies /v1/* to its BFF); the proxy's
// Admin area reaches it through `nexus-web`'s forwarder under `basePath` (`/api/admin/v1`,
// API-570), so a screen always calls the OpenAPI template `/v1/orgs/{orgId}/…`.
import type { paths } from "./schema";
import { NetworkError, toApiError } from "./errors";
import { CSRF_HEADER, isUnsafeMethod, type CsrfToken } from "./csrf";

type HttpMethod = "get" | "put" | "post" | "delete" | "patch";

/** Every path that has an operation for the method. */
export type PathsWith<M extends HttpMethod> = {
  [P in keyof paths]-?: [NonNullable<paths[P][M]>] extends [never] ? never : P;
}[keyof paths];

export type Operation<P extends keyof paths, M extends HttpMethod> = NonNullable<paths[P][M]>;

type PathParamsOf<O> = O extends { parameters: { path: infer X } } ? X : never;
type QueryOf<O> = O extends { parameters: { query?: infer X } } ? Exclude<X, undefined> : never;
type JsonBodyOf<R> = R extends { content: { "application/json": infer B } } ? B : never;
type BodyOptionOf<O> = O extends { requestBody: infer R }
  ? [JsonBodyOf<R>] extends [never] ? { body?: never } : { body: JsonBodyOf<R> }
  : O extends { requestBody?: infer R }
    ? [JsonBodyOf<NonNullable<R>>] extends [never] ? { body?: never } : { body?: JsonBodyOf<NonNullable<R>> }
    : { body?: never };

type SuccessStatus = 200 | 201 | 202 | 204;
type ResponsesOf<O> = O extends { responses: infer R } ? R : never;
type JsonOf<R> = R extends { content: { "application/json": infer J } } ? J : undefined;

/** The JSON body of the operation's success responses (undefined for 204). */
export type ResponseOf<O> = {
  [S in SuccessStatus & keyof ResponsesOf<O>]: JsonOf<ResponsesOf<O>[S]>;
}[SuccessStatus & keyof ResponsesOf<O>];

export type RequestOptions<O> = ([PathParamsOf<O>] extends [never] ? { path?: never } : { path: PathParamsOf<O> }) &
  ([QueryOf<O>] extends [never]
    ? { query?: never }
    : // A query with a required parameter (e.g. `periodQuery`) is itself required.
      object extends QueryOf<O> ? { query?: QueryOf<O> } : { query: QueryOf<O> }) &
  BodyOptionOf<O> & {
    signal?: AbortSignal;
    /** Request headers beside the client's own: `If-Match` on a settings write. */
    headers?: Record<string, string>;
  };

type OptionsArg<O> = object extends RequestOptions<O> ? [options?: RequestOptions<O>] : [options: RequestOptions<O>];

type QueryValue = string | number | boolean | null | undefined | readonly (string | number | boolean)[];

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface ApiClient {
  get<P extends PathsWith<"get">>(path: P, ...options: OptionsArg<Operation<P, "get">>): Promise<ResponseOf<Operation<P, "get">>>;
  post<P extends PathsWith<"post">>(path: P, ...options: OptionsArg<Operation<P, "post">>): Promise<ResponseOf<Operation<P, "post">>>;
  put<P extends PathsWith<"put">>(path: P, ...options: OptionsArg<Operation<P, "put">>): Promise<ResponseOf<Operation<P, "put">>>;
  patch<P extends PathsWith<"patch">>(path: P, ...options: OptionsArg<Operation<P, "patch">>): Promise<ResponseOf<Operation<P, "patch">>>;
  delete<P extends PathsWith<"delete">>(path: P, ...options: OptionsArg<Operation<P, "delete">>): Promise<ResponseOf<Operation<P, "delete">>>;
  /** Same-origin link to the CSV rendering of a list (`?format=csv`), for a plain download anchor. */
  csvUrl<P extends PathsWith<"get">>(path: P, ...options: OptionsArg<Operation<P, "get">>): string;
  /** Same-origin link to the PDF rendering of a document (`?format=pdf`), for a plain download anchor. */
  pdfUrl<P extends PathsWith<"get">>(path: P, ...options: OptionsArg<Operation<P, "get">>): string;
  /** A GET whose answer is bytes, not JSON (the gzip prompt body): the same session, retry and 401 handling. */
  getBytes<P extends PathsWith<"get">>(path: P, ...options: OptionsArg<Operation<P, "get">>): Promise<ArrayBuffer>;
  /** Called whenever any request answers 401 — the session is gone (FLOW-002 "signed out"). */
  onSessionExpired(listener: () => void): () => void;
}

export interface ClientConfig {
  csrf: CsrfToken;
  fetch?: FetchLike;
  /** Prefixed to every template path (no trailing slash); the shell's route to the API, default "". */
  basePath?: string;
}

/** Fill `{name}` placeholders (each value URI-encoded) and append the query string. */
export function buildUrl(template: string, pathParams: object | undefined, query: object | undefined): string {
  const params = (pathParams ?? {}) as Record<string, string | number>;
  const path = template.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`missing path parameter ${name}`);
    return encodeURIComponent(String(value));
  });
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries((query ?? {}) as Record<string, QueryValue>)) {
    if (value === undefined || value === null) continue;
    const values = Array.isArray(value) ? value : [value];
    for (const item of values) search.append(key, String(item));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") return undefined;
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("json")) return text;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/** The limiter's Retry-After in milliseconds: whole seconds, at least one, at most ten. */
function retryAfterMs(header: string | null): number {
  const seconds = Number(header ?? "1");
  return Math.min(Math.max(Number.isFinite(seconds) ? seconds : 1, 1), 10) * 1000;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    }, { once: true });
  });
}

/** Whether error is an aborted request or delay (an AbortError). */
export function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

export function createApiClient(config: ClientConfig): ApiClient {
  const doFetch: FetchLike = config.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const basePath = config.basePath ?? "";
  const expiredListeners = new Set<() => void>();
  /** The request URL: the shell's base path, the filled template, the caller's query plus any `format`. */
  const url = (template: string, options: RequestOptions<unknown> | undefined, extraQuery: object = {}): string =>
    buildUrl(basePath + template, options?.path, { ...(options?.query ?? {}), ...extraQuery });

  /** The request, with the one bounded 429 retry; a connection failure is a NetworkError. */
  async function perform(method: HttpMethod, template: string, options: RequestOptions<unknown> | undefined, accept: string): Promise<Response> {
    const headers: Record<string, string> = { ...options?.headers, Accept: accept };
    const upper = method.toUpperCase();
    const token = config.csrf.current();
    if (isUnsafeMethod(upper) && token !== null) headers[CSRF_HEADER] = token;
    const init: RequestInit = { method: upper, headers, credentials: "same-origin", cache: "no-store" };
    if (options?.body !== undefined) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(options.body);
    }
    if (options?.signal) init.signal = options.signal;

    let response: Response;
    try {
      const target = url(template, options);
      response = await doFetch(target, init);
      if (response.status === 429) {
        // The API's limiter refused the request before anything ran (API contract §5), so one
        // retry after its Retry-After — bounded — is safe for every method; a screen that fans
        // out several requests at once otherwise shows "rate limit exceeded" for a burst the
        // next second would have served.
        await sleep(retryAfterMs(response.headers.get("Retry-After")), options?.signal);
        response = await doFetch(target, init);
      }
    } catch (error) {
      if (isAbort(error)) throw error;
      throw new NetworkError();
    }
    return response;
  }

  /** A non-2xx answer as an ApiError; a 401 tells the session listeners first. */
  async function refuse(response: Response): Promise<never> {
    const body = await readBody(response);
    if (response.status === 401) for (const listener of [...expiredListeners]) listener();
    throw toApiError(response.status, body, response.headers.get("Retry-After"));
  }

  async function send(method: HttpMethod, template: string, options: RequestOptions<unknown> | undefined): Promise<unknown> {
    const response = await perform(method, template, options, "application/json");
    return response.ok ? readBody(response) : refuse(response);
  }

  async function sendForBytes(template: string, options: RequestOptions<unknown> | undefined): Promise<ArrayBuffer> {
    const response = await perform("get", template, options, "*/*");
    return response.ok ? response.arrayBuffer() : refuse(response);
  }

  // The overloads above carry the types; one untyped implementation serves every method.
  const call =
    (method: HttpMethod) =>
    (path: string, options?: RequestOptions<unknown>): Promise<never> =>
      send(method, path, options) as Promise<never>;

  return {
    get: call("get"),
    post: call("post"),
    put: call("put"),
    patch: call("patch"),
    delete: call("delete"),
    csvUrl: (path: string, options?: RequestOptions<unknown>) => url(path, options, { format: "csv" }),
    pdfUrl: (path: string, options?: RequestOptions<unknown>) => url(path, options, { format: "pdf" }),
    getBytes: (path: string, options?: RequestOptions<unknown>) => sendForBytes(path, options),
    onSessionExpired(listener) {
      expiredListeners.add(listener);
      return () => expiredListeners.delete(listener);
    },
  } as ApiClient;
}
